import { useEffect, useRef } from 'react';
import { useTour } from '../contexts/TourContext';
import { useAuth } from '../contexts/AuthContext';
import { getTourById } from '../data/tours';

export const useTours = () => {
  const { state, dispatch } = useTour();
  const { user, profile } = useAuth();
  const hasTriggered = useRef(new Set());

  const triggerTour = async (tourId, conditions = {}) => {
    // Check if user is authenticated
    if (!user) return;

    // Check if tour already completed or skipped
    if (state.completedTours.includes(tourId) || state.skippedTours.includes(tourId)) {
      return;
    }

    // Check if already triggered in this session
    if (hasTriggered.current.has(tourId)) {
      return;
    }

    // Check conditions
    const shouldTrigger = await checkConditions(conditions, tourId, profile);
    
    if (shouldTrigger) {
      hasTriggered.current.add(tourId);
      const tour = getTourById(tourId);
      if (tour) {
        dispatch({ type: 'START_TOUR', payload: tour });
      }
    }
  };

  const checkConditions = async (conditions, tourId, userProfile) => {
    // Check delay condition
    if (conditions.delay) {
      await new Promise(resolve => setTimeout(resolve, conditions.delay));
    }

    // Check if element is visible
    if (conditions.elementVisible) {
      const element = document.querySelector(conditions.elementVisible);
      if (!element) return false;
    }

    // Check user role
    if (conditions.userRole && conditions.userRole !== 'any') {
      if (userProfile?.role !== conditions.userRole) return false;
    }

    // Check first visit
    if (conditions.firstVisit) {
      const visitKey = `visited_${tourId}`;
      const hasVisited = localStorage.getItem(visitKey);
      if (hasVisited) return false;
      localStorage.setItem(visitKey, 'true');
    }

    // Check page path
    if (conditions.path) {
      const currentPath = window.location.pathname;
      if (!currentPath.includes(conditions.path)) return false;
    }

    return true;
  };

  const resetTour = (tourId) => {
    // Remove from completed and skipped
    const updatedCompleted = state.completedTours.filter(id => id !== tourId);
    const updatedSkipped = state.skippedTours.filter(id => id !== tourId);
    
    // Clear from localStorage
    if (user) {
      localStorage.setItem(`tours_${user.uid}`, JSON.stringify({
        completed: updatedCompleted,
        skipped: updatedSkipped
      }));
    }
    
    // Clear visit tracking
    localStorage.removeItem(`visited_${tourId}`);
    
    // Reset trigger tracking
    hasTriggered.current.delete(tourId);
    
    // Update state
    dispatch({ type: 'LOAD_SAVED_DATA', payload: { 
      completed: updatedCompleted, 
      skipped: updatedSkipped 
    }});
  };

  const stopCurrentTour = () => {
    dispatch({ type: 'STOP_TOUR' });
  };

  return { 
    triggerTour, 
    resetTour, 
    stopCurrentTour, 
    isRunning: state.isRunning,
    currentTour: state.currentTour,
    completedTours: state.completedTours,
    skippedTours: state.skippedTours
  };
};

// Hook for auto-triggering tours on component mount
export const useAutoTour = (tourId, conditions = {}) => {
  const { triggerTour } = useTours();
  const { user } = useAuth();
  const hasAttempted = useRef(false);

  useEffect(() => {
    if (user && !hasAttempted.current) {
      hasAttempted.current = true;
      
      const timer = setTimeout(() => {
        triggerTour(tourId, conditions);
      }, conditions.delay || 1000);

      return () => clearTimeout(timer);
    }
  }, [user, tourId, triggerTour, conditions]);
};

// Hook for manual tour triggers with button clicks
export const useManualTour = () => {
  const { triggerTour } = useTours();

  const startTour = (tourId) => {
    triggerTour(tourId, { 
      firstVisit: false, // Override first visit check for manual triggers
      delay: 0 
    });
  };

  return { startTour };
};

// Hook for conditional tour triggers based on user interactions
export const useConditionalTour = (tourId, triggerCondition, dependencies = []) => {
  const { triggerTour } = useTours();
  const { user } = useAuth();

  useEffect(() => {
    if (user && triggerCondition) {
      const timer = setTimeout(() => {
        triggerTour(tourId, { 
          firstVisit: false,
          delay: 500 
        });
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [user, triggerCondition, tourId, triggerTour, ...dependencies]);
};